import path from "node:path";
import { downloadImage, getFilenameFromUrl, type Locator, type Response, success, TaskState } from "@iflyrpa/share";
import { MockPublish } from "../../types";
import type { ParsedVerifyDecision } from "../../utils/douyin/verify-parser";
import { needsVerification, parseVerifyPassportDecision } from "../../utils/douyin/verify-parser";
import { ProxyAgent } from "../../utils/proxy";
import type { PublishAction } from "./index";

export const rpaAction_Server: PublishAction = async (task, params) => {
    const updateTaskState = task.taskStageStore?.update?.bind(task.taskStageStore, task.taskId || "");

    const tmpCachePath = task.getTmpPath();
    task.logger?.info("==>进入RPA操作：任务开始，下载图片开始...");

    // 预下载图片
    const imgs = params.coverImage ? [params.coverImage, ...(params.banners || [])] : params.banners || [];

    const imagePromise = Promise.all(
        imgs.map((url) => {
            const fileName = getFilenameFromUrl(url);
            return downloadImage(url, path.join(tmpCachePath, fileName));
        }),
    );

    // 获取代理信息
    let proxyUrl: string | undefined;
    if (params.localIP) {
        const args: [string, string | undefined, number | undefined] = [
            params.localIP,
            params.proxyLoc,
            params.accountId,
        ];
        task.logger?.info(`==> 开始获取代理信息：${args}`);
        const proxyAgentResult = await ProxyAgent({ logger: task.logger }, ...args);
        task.logger?.info("==> 代理信息获取成功！");
        proxyUrl = proxyAgentResult ? `http://${proxyAgentResult.ip}:${proxyAgentResult.port}` : undefined;
    }

    const commonCookies = {
        path: "/",
        secure: true,
        domain: "douyin.com",
        url: "https://creator.douyin.com",
        httpOnly: true,
    };

    task.logger?.info("==>开始打开抖音创作者页面...");
    const page = await task.createPage({
        show: task.debug,
        url: params.url || "https://creator.douyin.com/creator-micro/content/upload?default-tab=3",
        cookies: params.cookies?.map((it) => ({ ...it, ...commonCookies })),
        proxyUrl,
    });
    task.logger?.info("==>抖音创作者页面打开成功");

    await updateTaskState?.({
        state: TaskState.ACTION,
        connectAddress: task.steelConnector?.getProxyUrl(task.sessionId || "", "v1/sessions/debug"),
        sessionId: task.sessionId,
    });

    // ---- 工具函数 ----

    const waitForElement = async (selector: string, timeout = 10000): Promise<Locator | null> => {
        try {
            const element = page.locator(selector);
            await element.waitFor({ state: "visible", timeout });
            return element;
        } catch {
            task.logger?.warn(`元素未找到: ${selector}`);
            return null;
        }
    };

    const retryAction = async <T>(action: () => Promise<T>, maxRetries = 3, delay = 1000): Promise<T> => {
        let lastError: Error | undefined;
        for (let i = 0; i < maxRetries; i++) {
            try {
                return await action();
            } catch (error) {
                lastError = error as Error;
                task.logger?.warn(`重试 ${i + 1}/${maxRetries}: ${lastError.message}`);
                if (i < maxRetries - 1) {
                    await page.waitForTimeout(delay);
                }
            }
        }
        throw lastError;
    };

    const safeClick = async (locator: Locator | null): Promise<boolean> => {
        if (!locator) return false;
        try {
            await locator.click({ timeout: 5000 });
            return true;
        } catch (error) {
            task.logger?.warn(`点击失败: ${error}`);
            return false;
        }
    };

    const safeFill = async (locator: Locator | null, text: string): Promise<boolean> => {
        if (!locator || !text) return false;
        try {
            await locator.clear({ timeout: 3000 });
            await locator.fill(text, { timeout: 5000 });
            return true;
        } catch (error) {
            task.logger?.warn(`填充文本失败: ${error}`);
            return false;
        }
    };

    // ---- 自动化脚本 ----

    // 等待登录成功
    try {
        task.logger?.info("检查登录状态...");
        await page.waitForSelector("#micro", {
            state: "visible",
            timeout: 20000,
        });
        task.logger?.info("✓ 登录状态正常");
    } catch (_error) {
        task.logger?.error("✗ 登录失效或页面加载异常");
        await updateTaskState?.({ state: TaskState.FAILED, error: "登录失效" });
        return { code: 414, message: "登录失效", data: page.url() };
    }

    // 获取已下载图片
    const images = await imagePromise;
    task.logger?.info(`==>图片下载完成，共 ${images.length} 张，开始上传图片...`);

    // 上传图片
    if (images.length > 0) {
        await retryAction(async () => {
            const fileChooser = await page.waitForSelector('input[type="file"]', { timeout: 20000 });
            if (!fileChooser) {
                throw new Error("未找到图片上传输入框");
            }
            await fileChooser.setInputFiles(images);
            task.logger?.info(`图片上传成功: ${images.length} 张`);
            await page.waitForTimeout(2000);
        });
    }

    // 填写标题
    if (params.title) {
        task.logger?.info(`填写标题: ${params.title}`);
        await retryAction(async () => {
            const titleInstance = await waitForElement(".semi-input-wrapper input[placeholder='添加作品标题']", 50000);
            if (!titleInstance) {
                throw new Error("未找到标题输入框");
            }
            await titleInstance.click({ timeout: 5000 });
            await safeFill(titleInstance, params.title);
            task.logger?.info("标题填写完成");
        });
    }

    // 填写描述
    if (params.content) {
        task.logger?.info("填写描述");
        await page.waitForTimeout(1000);
        await retryAction(async () => {
            const descInstance = await waitForElement("div[data-placeholder='添加作品描述...']", 10000);
            if (!descInstance) {
                throw new Error("未找到描述编辑器");
            }
            await descInstance.click({ timeout: 5000 });
            await page.waitForTimeout(500);
            const cleanContent = params.content.replace(/#.*?\[.*?]#/g, "");
            await descInstance.fill(cleanContent);
            await page.keyboard.press("Control+End");
            task.logger?.info("描述填写完成");
        });
    }

    // 添加话题标签
    if (params.topic && params.topic.length > 0) {
        task.logger?.info(`添加话题标签: ${params.topic.length} 个`);
        try {
            const topics = params.topic;
            await retryAction(async () => {
                // const descInstance = page.locator(".editor-kit-container.editor.editor-comp-publish");
                for (const topic of topics) {
                    if (topic.cha_name) {
                        const topicTag = `#${topic.cha_name} `;
                        await page.keyboard.type(topicTag);
                        task.logger?.info(`已添加话题: ${topicTag.trim()}`);
                        await page.waitForTimeout(300);
                    }
                }
                task.logger?.info("✓ 话题标签添加完成");
            });
        } catch (error) {
            task.logger?.warn(`添加话题标签失败: ${error}`);
        }
    }

    // 选择合集
    if (params.mix?.mix_name) {
        task.logger?.info(`选择合集: ${params.mix.mix_name}`);
        try {
            const instanceCollection = page.locator("[class^='mix-sel-wrap'] .semi-select").nth(1);
            await instanceCollection.click({ timeout: 5000 });
            await page.waitForTimeout(2000);
            await page
                .locator(".semi-select-option-list .semi-select-option")
                .filter({ hasText: params.mix.mix_name })
                .first()
                .click({ force: true, timeout: 5000 });
        } catch (error) {
            task.logger?.warn(`选择合集失败: ${error}`);
        }
    }

    // 选择合集集数
    if (params.mix?.mix_order_name) {
        task.logger?.info(`选择合集集数: ${params.mix.mix_order_name}`);
        try {
            const instanceCollection = page.locator("[class^='mix-sel-wrap'] .semi-select").nth(2);
            await instanceCollection.click({ timeout: 5000 });
            await page.waitForTimeout(2000);
            await page
                .locator(".mix-order-dropdown .semi-select-option")
                .filter({ hasText: params.mix.mix_order_name })
                .first()
                .click({ force: true, timeout: 5000 });
        } catch (error) {
            task.logger?.warn(`选择合集集数失败: ${error}`);
        }
    }

    // 选择音乐
    if (params.musicId && params.musicName) {
        task.logger?.info(`选择音乐: ${params.musicName} - ${params.musicAuthor || ""}`);
        try {
            await retryAction(async () => {
                const musicTrigger = await waitForElement("[class^='container-right-']", 10000);
                if (!musicTrigger) throw new Error("未找到音乐选择触发器");
                await musicTrigger.click({ timeout: 5000 });
                task.logger?.info("已打开音乐选择面板");
                await page.waitForTimeout(1000);

                const searchBox = await waitForElement("[class^='music-search']", 10000);
                if (!searchBox) throw new Error("未找到音乐搜索框");
                await searchBox.click({ timeout: 5000 });
                await page.waitForTimeout(500);

                const searchInput = page.locator("[class^='music-search'] input");
                const searchText = params.musicAuthor
                    ? `${params.musicName} - ${params.musicAuthor}`
                    : params.musicName;
                await searchInput.fill(searchText || "");
                task.logger?.info(`已输入搜索内容: ${searchText}`);
                await page.waitForTimeout(300);
                await searchInput.press("Enter");
                task.logger?.info("已触发搜索");
                await page.waitForTimeout(4000);

                const musicCards = page.locator("[class^='card-container-']");
                const matchedCard = musicCards.filter({ hasText: params.musicName }).first();
                const cardCount = await matchedCard.count();
                if (cardCount === 0) throw new Error(`未找到匹配的音乐: ${params.musicName}`);

                task.logger?.info("找到匹配的音乐，准备选择");
                await matchedCard.hover();
                await page.waitForTimeout(2000);

                const selectButton = matchedCard.locator(".semi-button").first();
                const buttonClicked = await safeClick(selectButton);
                if (!buttonClicked) throw new Error("点击音乐选择按钮失败");

                task.logger?.info("✓ 音乐选择完成");
                await page.waitForTimeout(500);
            });
        } catch (error) {
            task.logger?.warn(`选择音乐失败: ${error}`);
        }
    }

    // 选择热点
    if (params.hotSentence) {
        task.logger?.info(`选择热点: ${params.hotSentence}`);
        try {
            const instance = page.locator(".semi-select-filterable").nth(1);
            await instance.click({ timeout: 5000 });
            await page.waitForTimeout(1000);
            await page.locator(".semi-input-wrapper input").nth(1).fill(params.hotSentence);
            await page.waitForTimeout(2000);
            await page
                .locator(".semi-popover-content .semi-select-option")
                .filter({ hasText: params.hotSentence })
                .first()
                .click({ force: true, timeout: 5000 });
        } catch (error) {
            task.logger?.warn(`选择热点失败: ${error}`);
        }
    }

    // 设置可见范围
    if (params.visibleRange) {
        task.logger?.info(`设置可见范围: ${params.visibleRange}`);
        try {
            if (params.visibleRange === "2") {
                await page.locator("label:has-text('好友可见')").first().click({ timeout: 5000 });
                task.logger?.info("已设置为好友可见");
            } else if (params.visibleRange === "1") {
                await page.locator("label:has-text('仅自己可见')").first().click({ timeout: 5000 });
                task.logger?.info("已设置为仅自己可见");
            }
        } catch (error) {
            task.logger?.warn(`设置可见范围失败: ${error}`);
        }
    }

    // 设置保存权限
    if (!params.allowSave) {
        task.logger?.info("设置保存权限：不允许");
        try {
            await page.locator("label:has-text('不允许')").first().click({ timeout: 5000 });
            task.logger?.info("已设置为不允许保存");
            await page.waitForTimeout(300);
        } catch (error) {
            task.logger?.warn(`设置保存权限失败: ${error}`);
        }
    }

    // 设置定时发布
    if (!params.isImmediatelyPublish && params.scheduledPublish) {
        task.logger?.info("设置定时发布");
        try {
            await page.locator('label:has-text("定时发布")').first().click({ timeout: 5000 });
            task.logger?.info("已选择定时发布");
            await page.waitForTimeout(500);

            const timestamp =
                params.scheduledPublish > 10000000000 ? params.scheduledPublish / 1000 : params.scheduledPublish;
            const publishDate = new Date(timestamp * 1000);

            const year = publishDate.getFullYear();
            const month = String(publishDate.getMonth() + 1).padStart(2, "0");
            const day = String(publishDate.getDate()).padStart(2, "0");
            const hour = String(publishDate.getHours()).padStart(2, "0");
            const minute = String(publishDate.getMinutes()).padStart(2, "0");
            const formattedTime = `${year}-${month}-${day} ${hour}:${minute}`;

            task.logger?.info(`目标发布时间: ${formattedTime}`);

            const releaseTimeInstance = await waitForElement(
                ".semi-datepicker-input input[placeholder='日期和时间']",
                10000,
            );
            if (!releaseTimeInstance) throw new Error("未找到时间输入框");

            await releaseTimeInstance.click({ timeout: 5000 });
            await page.waitForTimeout(500);
            await releaseTimeInstance.fill(formattedTime);
            await page.waitForTimeout(500);

            const inputValue = await releaseTimeInstance.inputValue();
            if (inputValue !== formattedTime) {
                task.logger?.warn(`时间输入验证失败，期望: ${formattedTime}, 实际: ${inputValue}`);
            } else {
                task.logger?.info("定时发布设置完成");
            }
            await releaseTimeInstance.blur();
            await page.waitForTimeout(500);
        } catch (error) {
            task.logger?.warn(`定时发布设置失败: ${error}`);
        }
    }

    // 模拟发布（测试用）
    if (MockPublish) {
        const message = "文章模拟发布成功";
        const data = "mock_item_id";
        await updateTaskState?.({
            state: TaskState.SUCCESS,
            result: { response: data },
        });
        return success(data, message);
    }

    // 点击发布按钮并监听接口响应
    task.logger?.info("准备发布...");
    await page.waitForTimeout(1000);

    interface PublishResult {
        itemId: string;
        decision: ParsedVerifyDecision | null;
    }

    const result = await new Promise<PublishResult>((resolve, reject) => {
        const handleResponse = async (res: Response) => {
            if (res.url().includes("/web/api/media/aweme/create_v2")) {
                task.logger?.info("匹配到发布接口响应");

                // 解析二次验证决策头：抖音要求二次验证时仍返回 status_code === 0，
                // 真正的信号在 x-tt-verify-passport-decision 响应头（account_flow === "verify"），
                // 此时文章并未真正发布，必须按失败处理。
                let decision: ParsedVerifyDecision | null = null;
                try {
                    const headers = await res.allHeaders();
                    decision = parseVerifyPassportDecision(headers["x-tt-verify-passport-decision"]);
                } catch (error) {
                    task.logger?.warn(`解析验证决策头失败: ${error}`);
                }

                const jsonResponse = await res.json();
                task.logger?.info(`发布接口响应数据: ${JSON.stringify(jsonResponse)}`);
                page.off("response", handleResponse);

                if (needsVerification(decision)) {
                    task.logger?.warn("检测到需要二次验证，文章未真正发布");
                    resolve({ itemId: "", decision });
                    return;
                }

                if (jsonResponse?.status_code !== 0) {
                    reject(new Error(jsonResponse?.status_msg || "发布失败"));
                    return;
                }

                // status_code === 0 但缺少 item_id 视为未真正发布，不能误判为成功
                const itemId = jsonResponse?.data?.item_id || jsonResponse?.item_id || "";
                if (!itemId) {
                    reject(new Error(jsonResponse?.status_msg || "发布异常：未返回作品ID，文章可能未真正发布"));
                    return;
                }

                resolve({ itemId, decision: null });
            }
        };

        page.on("response", handleResponse);

        setTimeout(() => {
            page.locator("#DCPF button:has-text('发布')").first().click();
            task.logger?.info("发布按钮已点击");
        }, 1000);

        // 整体超时保护
        setTimeout(() => {
            page.off("response", handleResponse);
            reject(new Error("发布超时，未收到接口响应"));
        }, 30000);
    }).catch(async (err) => {
        const msg = `发布失败：${err?.message || "未知错误"}`;
        task.logger?.error(msg);
        await updateTaskState?.({ state: TaskState.FAILED, error: msg });
        await page.close();
        return null;
    });

    if (!result) {
        return { code: 414, message: "发布失败", data: "" };
    }

    // 需要二次验证：文章未真正发布，返回验证信息供上层触发扫码验证流程
    if (needsVerification(result.decision)) {
        const decision = result.decision;
        const details = [decision?.verify_title, decision?.verify_desc].filter(Boolean).join(" ");
        const message = `[douyinPublish rpa-server]发布失败，需要二次验证${details ? `：${details}` : ""}`;
        task.logger?.warn(message);
        await updateTaskState?.({
            state: TaskState.FAILED,
            error: message,
            result: { data: decision },
        });
        await page.close();
        return { code: 414, message, data: "" };
    }

    task.logger?.info(`✓ 发布成功，item_id: ${result.itemId}`);

    await updateTaskState?.({
        state: TaskState.SUCCESS,
        result: { response: result.itemId },
    });

    await page.close();
    return success(result.itemId);
};
